/**
 * Types + helpers partagés du widget « Couverture des commandes » (compact +
 * dialog + export Excel). Sémantique identique à la page Pilotage de
 * préparation (base **stock éligible**).
 */
import { CouvertureProduitRaw } from "../../models/data.model";

/** Statut de couverture d'un produit (3 états mutuellement exclusifs). */
export type CouvertureStatut = "COUVERT" | "A_RESERVER" | "MANQUE";

export const COUVERTURE_STATUT_LABEL: Record<CouvertureStatut, string> = {
  COUVERT: "Couvert",
  A_RESERVER: "À réserver",
  MANQUE: "En manque",
};

/**
 * Ligne normalisée consommée par le widget + l'export Excel. Toutes les
 * quantités sont en **UG** sauf `besoinUV` / unités de vente.
 */
export interface CouvertureCommandeRow {
  articleId: string;
  reference: string | null;
  denomination: string | null;
  uniteArticle: string | null; // UG
  uniteVente: string | null; // UV
  coefficient: number; // UG par UV
  besoinUG: number; // commandé en UG
  besoinUV: number; // commandé en UV
  engageUG: number; // déjà réservé (zone prépa)
  stockEligibleUG: number; // réservable maintenant
  stockTotalUG: number; // stock physique dispo total
  qtyEnOFUG: number; // en production
  nbCommandes: number;
  nbLignes: number;
  nbLignesARevoir: number;
  premierDeparture: string | null; // ISO (YYYY-MM-DD)
  premiereExpedition: string | null; // ISO — 1ʳᵉ date d'expédition prévue
  premiereLivraison: string | null; // ISO — 1ʳᵉ date de livraison prévue
  /** Durée DDM/DLC de l'article en jours (contrainte de fraîcheur). */
  dluoJours: number | null;
  typeDateLimite: "DDM" | "DLC" | "AUCUNE" | string | null;
  isPrestation: boolean;
}

export interface CouvertureExportContext {
  horizonLabel: string;
  clientsLabel?: string;
  groupesLabel?: string;
  generatedAt?: Date;
}

export interface CouvertureDerived {
  reste: number; // commandé − déjà réservé
  reservableNow: number; // min(reste, éligible)
  manque: number; // max(0, reste − éligible)
  couvrablePct: number; // (réservé + éligible) / commandé
  statut: CouvertureStatut;
}

/** Dérivés de couverture, base stock éligible (cf. page Pilotage). */
export function deriveCouverture(row: CouvertureCommandeRow): CouvertureDerived {
  const elig = Math.max(0, row.stockEligibleUG);
  const reste = Math.max(0, row.besoinUG - row.engageUG);
  const reservableNow = Math.min(reste, elig);
  const manque = Math.max(0, reste - elig);
  const couvrablePct =
    row.besoinUG > 0
      ? Math.round(
          (Math.min(row.besoinUG, row.engageUG + elig) / row.besoinUG) * 100,
        )
      : 100;
  const statut: CouvertureStatut =
    reste <= 0 ? "COUVERT" : manque > 0 ? "MANQUE" : "A_RESERVER";
  return { reste, reservableNow, manque, couvrablePct, statut };
}

/** UG → UV (garde-fou coef ≤ 0). */
export function toUV(qtyUG: number, coef: number): number {
  const c = coef && coef > 0 ? coef : 1;
  return qtyUG / c;
}

/**
 * Mappe la ligne brute du back (`PilotageProduitRow` exposée via
 * `CouvertureProduitRaw`) vers la ligne normalisée du widget.
 * `besoinTotal` est en UV → `besoinUG = besoinTotal × coefficient`.
 */
export function mapRawToRow(raw: CouvertureProduitRaw): CouvertureCommandeRow {
  const coef = raw.coefficient && raw.coefficient > 1 ? raw.coefficient : 1;
  return {
    articleId: raw.articleId,
    reference: raw.reference ?? null,
    denomination: raw.articleDenomination ?? null,
    uniteArticle: raw.uniteArticle ?? null,
    uniteVente: raw.uniteVente ?? null,
    coefficient: coef,
    besoinUV: raw.besoinTotal ?? 0,
    besoinUG: (raw.besoinTotal ?? 0) * coef,
    engageUG: raw.qtyEngageePhysique ?? 0,
    stockEligibleUG: raw.stockDispoEligible ?? 0,
    stockTotalUG: raw.stockDispo ?? 0,
    qtyEnOFUG: raw.qtyEnOF ?? 0,
    nbCommandes: raw.nbCommandes ?? 0,
    nbLignes: raw.nbLignes ?? 0,
    nbLignesARevoir: raw.nbLignesARevoir ?? 0,
    premierDeparture: raw.premierDeparture ?? null,
    premiereExpedition: raw.premiereExpedition ?? raw.premierDeparture ?? null,
    premiereLivraison: raw.premiereLivraison ?? null,
    dluoJours: raw.dluoJours ?? null,
    typeDateLimite: raw.typeDateLimite ?? null,
    isPrestation: raw.isPrestation ?? false,
  };
}
